import React, { useContext, useEffect, useMemo, useState } from "react";
import { httpGet } from "../../components/helpers/httpMethods";
import { PaymentContext } from "../../context/paymentContext";
import { noticeFor } from "../changeOfName/ConApply";

// My applications (Change of Name) in the account hub. Reads the same list as the old page (con/user/applications);
// newspaper names, logos and prices come from con/vendors. Unpaid applications can be paid from here (same payment step).
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : String(s || "").trim());
const day = (d) => { const x = d ? new Date(d) : null; return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" }) : ""; };
const nm = (...p) => p.map((x) => String(x || "").trim()).filter(Boolean).join(" ");
const categoryOf = (a) => { const m = String(a.reasonDescription || "").match(/^Category:\s*([^.]+)/); return m ? m[1].trim() : "Change of Name"; };
const turn = (t) => { const n = parseInt(String(t || ""), 10); return Number.isFinite(n) && n > 0 ? n : 3; };

function stageOf(a) {
  const s = String(a.status || "").toLowerCase();
  if (s === "cancelled") return 5;
  if (s === "completed") return 4;
  if (s === "started") return 3;
  if (s === "paid") return 2;
  return 1;
}

export function Applications({ history }) {
  const pay = useContext(PaymentContext) || {};
  const [apps, setApps] = useState(null);
  const [offers, setOffers] = useState({});
  const [tab, setTab] = useState("progress");
  const [open, setOpen] = useState("");
  const [err, setErr] = useState("");
  const [outcomes, setOutcomes] = useState({});
  useEffect(() => {
    httpGet("con/outcomes?as=reader").then((r) => {
      const list = (r && r.data && r.data.outcomes) || [];
      setOutcomes(Object.fromEntries(list.map((x) => [x.applicationId, x])));
    });
    httpGet("con/user/applications").then((r) => {
      if (r && r.data && Array.isArray(r.data.conApplications)) return setApps(r.data.conApplications);
      const msg = (r && r.er && (r.er.message || String(r.er))) || "";
      if (/no .*application|not found/i.test(msg)) return setApps([]);
      setErr(msg || "We couldn’t load your applications."); setApps([]);
    });
    httpGet("con/vendors").then((r) => {
      const map = {};
      ((r && r.data && r.data.conSubscriptions) || []).forEach((o) => { map[o.publicationId] = o; });
      setOffers(map);
    });
  }, []);
  const groups = useMemo(() => {
    const list = [...(apps || [])].sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt));
    return {
      progress: list.filter((a) => [2, 3].includes(stageOf(a))),
      done: list.filter((a) => stageOf(a) === 4),
      closed: list.filter((a) => stageOf(a) === 5),
      unpaid: list.filter((a) => stageOf(a) === 1 && Date.now() - new Date(a.createdAt) < 180 * 864e5),
    };
  }, [apps]);
  useEffect(() => { if (apps && !groups.progress.length && groups.done.length) setTab("done"); }, [apps]);
  const list = groups[tab] || [];
  const payNow = (a) => {
    const o = offers[a.publicationId];
    if (!o || !pay.CONinitPayment) return;
    pay.CONinitPayment({ publicationId: a.publicationId, id: a.id, currency: "NGN", amount: `${Number(o.price) + Number(o.serviceCharge || 0)}`, type: "CARD" });
  };

  return (
    <>
      <div className="ac-title"><h1>My applications</h1><p>Your Change of Name notices: where each one is, and the published page once it’s printed.</p></div>
      <div className="ac-bar">
        <div className="ac-tabs" role="tablist">
          {[["progress", "In progress"], ["done", "Published"], ...(apps && groups.closed.length ? [["closed", "Closed"]] : []), ["unpaid", "Not paid"]].map(([k, l]) => (
            <button type="button" key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l} ({apps ? groups[k].length : "…"})</button>
          ))}
        </div>
        <span className="ac-grow" />
        <button type="button" className="ac-btn2" onClick={() => history.push("/changeofname")}>New application</button>
      </div>
      {apps === null ? <p className="ac-muted">Loading your applications…</p> : err ? <div className="ac-card"><p className="ac-muted">{err}</p></div> : !list.length ? (
        <div className="ac-card ac-empty">
          <p>{tab === "closed" ? "No closed orders." : tab === "progress" ? "No applications in progress." : tab === "done" ? "No published notices yet. Once a newspaper prints yours, you can download the page here." : "No unpaid applications."}</p>
          {tab !== "unpaid" ? <button type="button" className="ac-btn" onClick={() => history.push("/changeofname")}>Publish a change of name</button> : null}
        </div>
      ) : list.map((a) => {
        const o = offers[a.publicationId];
        const paper = o && o.publication ? titleCase(o.publication.name) : "Newspaper";
        const st = stageOf(a);
        const cat = categoryOf(a);
        const notice = noticeFor({ category: cat, formerTitle: a.formerTitle, formerFirstname: a.formerFirstname, formerMiddlename: a.formerMiddlename, formerLastname: a.formerLastname,
          newTitle: a.newTitle, newFirstname: a.newFirstname, newMiddlename: a.newMiddlename, newLastname: a.newLastname, formalDateOfBirth: a.formalDateOfBirth, newDateOfBirth: a.newDateOfBirth });
        const paidAt = a.updatedAt || a.createdAt;
        const dueBy = (() => { const d = new Date(paidAt); d.setDate(d.getDate() + turn(o && o.turnAroundTime)); return d; })();
        const oc = outcomes[a.id];
        const line = st === 5 ? `${paper} closed this order${oc && oc.reason ? `: ${oc.reason}` : ""}. ${oc && oc.refundStatus === "refunded" ? `We’ve refunded ${naira(oc.refundAmount)}.` : oc && oc.refundAmount ? `We’ll refund ${naira(oc.refundAmount)} and email you when it’s done.` : ""}`
          : st === 4 ? `Published${a.publishedDate ? ` on ${day(a.publishedDate)}` : ""} in ${paper}${oc && oc.pageNumber ? `, page ${oc.pageNumber}` : ""}.`
          : st === 3 ? `Scheduled to appear in ${paper}${a.publishedDate ? ` on ${day(a.publishedDate)}` : ""}.`
          : st === 2 ? `With ${paper}. They’ll confirm the date soon${o ? ` (usually by ${day(dueBy)})` : ""}.`
          : "Not paid yet, so it hasn’t been sent to the newspaper.";
        const steps = ["Submitted", "Paid", "Scheduled", "Published"];
        return (
          <div className={`ac-card ac-app${st === 4 ? " done" : ""}`} key={a.id}>
            <div className="ac-app-top">
              <span className="ac-app-logo">{o && o.publication && (o.publication.logo || o.publication.image) ? <img src={o.publication.logo || o.publication.image} alt="" /> : null}</span>
              <span className="ac-app-main">
                <small className="ac-app-cat">{cat}</small>
                <strong>{nm(a.formerFirstname, a.formerLastname)} <span aria-hidden="true">→</span> {nm(a.newFirstname, a.newLastname)}</strong>
                <small>Ref {String(a.id).slice(0, 8).toUpperCase()} · applied {day(a.createdAt)}</small>
              </span>
            </div>
            {st !== 5 ? <ol className="ac-track" aria-label="Progress">
              {steps.map((s, i) => <li key={s} className={i + 1 <= st ? "done" : ""}><span>{i + 1 <= st ? "✓" : i + 1}</span>{s}</li>)}
            </ol> : null}
            <p className="ac-app-line">{line}</p>
            {st === 4 && oc && oc.kind === "outside" ? <p className="ac-muted">Confirmed by the newspaper. No copy of the page was attached.</p> : null}
            {st === 4 && !a.proveOfCompletion ? <p className="ac-muted">Need a copy? {oc && oc.editionUrl ? "Open that day’s edition here, or buy" : "Buy"} the printed paper from a newspaper vendor near you.</p> : null}
            <div className="ac-app-actions">
              {st === 4 && a.proveOfCompletion ? <a className="ac-btn ac-btn-a" href={a.proveOfCompletion} target="_blank" rel="noreferrer">Download the published page</a> : null}
              {st === 4 && oc && oc.editionUrl ? <button type="button" className="ac-btn ac-btn-a" onClick={() => history.push(oc.editionUrl)}>Read or buy that day’s paper</button> : null}
              {st === 4 && !a.proveOfCompletion && !oc ? <small className="ac-muted">The newspaper hasn’t attached the page yet. Contact support if you need it.</small> : null}
              {st === 5 ? <button type="button" className="ac-btn" onClick={() => history.push("/changeofname")}>Apply with another newspaper</button> : null}
              {st === 1 && o ? <button type="button" className="ac-btn" onClick={() => payNow(a)}>Pay {naira(Number(o.price) + Number(o.serviceCharge || 0))} to submit</button> : null}
              {st === 1 && !o ? <small className="ac-muted">This newspaper isn’t taking notices now. Start a new application with another paper.</small> : null}
              <button type="button" className="ac-link" onClick={() => setOpen(open === a.id ? "" : a.id)}>{open === a.id ? "Hide notice" : "See the notice"}</button>
            </div>
            {open === a.id ? <div className="ac-print"><b>{notice.head}</b><br />{notice.body}</div> : null}
          </div>
        );
      })}
    </>
  );
}
